/**
 * AssetSummaryCard — Furnace-K-style header for one staged asset in
 * `AssetActivitiesStep`. Renders the asset's metadata + per-type fields
 * + the count of staged activities, mirroring the asset detail page's
 * header layout so the wizard preview and the post-submit detail page
 * stay visually aligned.
 *
 * Takes a `StagedAsset` directly — no read off `FormData`. The asset
 * wizard now stages N assets (manual = 1, bulk = N), so each rendered
 * card reads its own block from `formData.stagedAssets[i]`.
 *
 * Per-type field set is built by the shared `buildAssetSummaryFields`
 * helper in `components/asset-list/assetSummaryFields.tsx` so this
 * card and the Manage Assets list `AssetCard` stay aligned without
 * parallel edits.
 */

import { ChevronRight, ChevronDown } from 'lucide-react';
import { COUNTRY_CODES } from '@g17eco/emissions-config';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { AssetType } from '../../types/emissions';
import { ASSET_TYPE_BRANDING, getAssetTypeBrand } from '../../config/assetTypeBranding';
import { type StagedAsset } from './types';
import {
  buildAssetSummaryFields,
  SummaryField,
} from '../asset-list/assetSummaryFields';

interface AssetSummaryCardProps {
  staged: StagedAsset;
  onClick: () => void;
  isOpen: boolean;
}

const countryName = (code?: string): string => {
  if (!code) {
    return '';
  }
  return COUNTRY_CODES.find(([c]) => c === code)?.[1] ?? code;
};

export const AssetSummaryCard = ({ staged, onClick, isOpen }: AssetSummaryCardProps) => {
  const { asset, activities } = staged;
  const assetType = asset.assetType as AssetType;
  const brand = assetType ? ASSET_TYPE_BRANDING[assetType] : getAssetTypeBrand('');
  const HeaderIcon = brand.Icon;

  const country = countryName(asset.region);
  const fields = buildAssetSummaryFields(asset);

  const activityCount = activities.length;
  const ChevronIcon = isOpen ? ChevronDown : ChevronRight;

  return (
    <div className='tw:py-3 tw:flex tw:items-center tw:gap-4 tw:cursor-pointer' onClick={onClick}>
      <div
        className={cn(
          'tw:flex tw:items-center tw:justify-center tw:rounded-full tw:size-12 tw:shrink-0',
          brand.iconBg,
        )}
      >
        <HeaderIcon className={cn('tw:size-6', brand.iconColor)} />
      </div>
      <div className='tw:flex tw:flex-col tw:justify-center tw:items-start tw:gap-1 tw:flex-1 tw:min-w-0'>
        <h3 className='tw:text-base tw:font-semibold tw:my-0'>{asset.name || 'New Asset'}</h3>
        <div className='tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground tw:flex-wrap'>
          <Badge variant='outline' className={cn('tw:text-xs tw:font-semibold', brand.iconColor)}>
            {brand.label}
          </Badge>
          {country && <Badge variant='outline'>{country}</Badge>}
          {staged.bulkGroupId && <Badge>File upload</Badge>}
          <span className='tw:ml-auto'>
            {activityCount} {activityCount === 1 ? 'activity' : 'activities'}
          </span>
        </div>
        {fields.length > 0 && (
          <div className='tw:mt-2 tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-4 tw:gap-x-4 tw:gap-y-1 tw:w-full'>
            {fields.map((field) => (
              <SummaryField
                key={field.label}
                Icon={field.Icon}
                label={field.label}
                value={field.value}
                iconColor={brand.iconColor}
              />
            ))}
          </div>
        )}
      </div>
      <div className='tw:flex tw:items-center tw:justify-end'>
        <ChevronIcon className='tw:size-8 tw:text-muted-foreground' />
      </div>
    </div>
  );
};
